(function (root, factory) {
  'use strict';

  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.scUpsellCore = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  function createSerialQueue() {
    var tail = Promise.resolve();

    return function enqueue(task) {
      var run = tail.then(task, task);
      tail = run.catch(function () {});
      return run;
    };
  }

  function copyDesired(desired) {
    var variantId = String(desired && desired.variantId || '');
    if (!/^\d+$/.test(variantId)) {
      throw makeError('E_SC_UPSELL_VARIANT', 'Upsell variant is invalid');
    }
    return {
      checked: !!(desired && desired.checked),
      variantId: variantId
    };
  }

  function normalizeOfferConfig(input) {
    var source = input || {};
    var offerId = String(source.offerId || '');
    var behavior = String(source.behavior || 'before');
    var relationship = String(source.relationship || 'top_level');
    var kind = String(source.kind || 'cross_sell');
    var position = String(source.position || 'before');
    var quantityPolicy = String(source.quantityPolicy || 'one');
    var allowedKinds = ['cross_sell', 'protection', 'warranty', 'gift_wrap', 'addon'];

    if (!/^[A-Za-z0-9._:-]{1,120}$/.test(offerId)) {
      throw makeError('E_SC_UPSELL_OFFER_ID', 'Upsell offer identity is invalid');
    }
    if (behavior !== 'before' && behavior !== 'after') {
      throw makeError('E_SC_UPSELL_BEHAVIOR', 'Upsell behavior must be before or after');
    }
    if (relationship !== 'top_level' && relationship !== 'strict_child') {
      throw makeError('E_SC_UPSELL_RELATIONSHIP', 'Upsell relationship is unsupported');
    }
    if (behavior === 'before' && relationship === 'strict_child') {
      throw makeError(
        'E_SC_UPSELL_BEFORE_CHILD',
        'A strict child cannot be added before its new parent exists'
      );
    }
    if (allowedKinds.indexOf(kind) === -1) {
      throw makeError('E_SC_UPSELL_KIND', 'Upsell kind is unsupported');
    }
    if (position !== 'before' && position !== 'after') {
      throw makeError('E_SC_UPSELL_POSITION', 'Upsell position is unsupported');
    }
    if (quantityPolicy !== 'one') {
      throw makeError('E_SC_UPSELL_QUANTITY_POLICY', 'Only one offer unit per parent is supported');
    }

    return Object.freeze({
      offerId: offerId,
      behavior: behavior,
      relationship: relationship,
      kind: kind,
      position: position,
      quantityPolicy: quantityPolicy,
      nested: behavior === 'after' && relationship === 'strict_child',
      direct: behavior === 'before' && relationship === 'top_level'
    });
  }

  function createIntentController(adapter) {
    var desired = null;
    var revision = 0;
    var flight = null;

    function request(nextDesired) {
      desired = copyDesired(nextDesired);
      revision += 1;
      if (flight) return flight;

      flight = drain().finally(function () {
        flight = null;
      });
      return flight;
    }

    async function drain() {
      if (adapter.onBusy) adapter.onBusy(true);
      try {
        while (true) {
          var currentRevision = revision;
          var currentDesired = copyDesired(desired);
          await adapter.enqueue(function () {
            return adapter.converge(currentDesired);
          });

          if (currentRevision !== revision) continue;
          if (adapter.refresh) {
            await adapter.enqueue(function () {
              return adapter.refresh();
            });
          }
          if (currentRevision === revision) break;
        }
        if (adapter.onSettled) adapter.onSettled(copyDesired(desired));
        return copyDesired(desired);
      } catch (error) {
        if (adapter.recover) {
          try {
            await adapter.enqueue(function () {
              return adapter.recover(error);
            });
          } catch (recoveryError) {
            error.recoveryError = recoveryError;
          }
        }
        if (adapter.onError) adapter.onError(error);
        throw error;
      } finally {
        if (adapter.onBusy) adapter.onBusy(false);
      }
    }

    return {
      request: request,
      get pending() { return !!flight; },
      get desired() { return desired && copyDesired(desired); }
    };
  }

  function createCartTransport(options) {
    var fetchFn = options.fetch;
    var offerId = String(options.offerId || '');
    var offerKind = String(options.offerKind || 'cross_sell');
    var triggerProductId = String(options.triggerProductId || '');
    var parentOperationId = String(options.parentOperationId || '');
    var parentKey = String(options.parentKey || '');
    var cartUrl = options.cartUrl || '/cart.js';
    var addUrl = options.addUrl || '/cart/add.js';
    var changeUrl = options.changeUrl || '/cart/change.js';
    var maxOwnedLines = options.maxOwnedLines || 4;
    var requestTimeoutMs = options.requestTimeoutMs || 20000;

    if (!offerId || typeof fetchFn !== 'function') {
      throw makeError('E_SC_UPSELL_CONFIG', 'Upsell transport is not configured');
    }

    function ownedLines(cart) {
      return (cart.items || []).filter(function (item) {
        var properties = item && item.properties || {};
        if (String(properties._sc_offer_id || '') !== offerId) return false;
        if (parentOperationId) {
          var binding = properties._sc_offer_parent_operation_id || properties._pz_parent_operation_id || '';
          if (String(binding) !== parentOperationId) return false;
        }
        if (parentKey) {
          var relationship = item.parent_relationship || {};
          var nativeParent = relationship.parent_key
            || relationship.parent && relationship.parent.key
            || '';
          if (String(nativeParent) !== parentKey) return false;
        }
        return true;
      });
    }

    function exactLine(lines, variantId, operationId) {
      if (lines.length !== 1) return false;
      var line = lines[0];
      var properties = line.properties || {};
      if (String(line.variant_id) !== String(variantId) || Number(line.quantity) !== 1) return false;
      if (String(properties._sc_offer_id || '') !== offerId) return false;
      if (String(properties._sc_offer_kind || '') !== offerKind) return false;
      if (triggerProductId
        && String(properties._sc_offer_trigger_product_id || '') !== triggerProductId) return false;
      if (parentOperationId
        && String(properties._sc_offer_parent_operation_id || properties._pz_parent_operation_id || '') !== parentOperationId) return false;
      if (operationId && String(properties._sc_offer_operation_id || '') !== operationId) return false;
      return true;
    }

    async function readCart() {
      var response = await fetchWithTimeout(cartUrl, {
        headers: { Accept: 'application/json' }
      });
      if (!response || !response.ok) {
        throw makeError('E_SC_UPSELL_CART_READ', 'Cart state is unavailable', response && response.status);
      }
      var cart = await response.json();
      if (!cart || !Array.isArray(cart.items)) {
        throw makeError('E_SC_UPSELL_CART_SHAPE', 'Cart state is invalid');
      }
      return cart;
    }

    async function write(url, body) {
      return fetchWithTimeout(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
    }

    async function fetchWithTimeout(url, init) {
      var controller = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = null;
      if (controller) {
        init = Object.assign({}, init, { signal: controller.signal });
        timer = setTimeout(function () { controller.abort(); }, requestTimeoutMs);
      }
      try {
        return await fetchFn(url, init);
      } finally {
        if (timer) clearTimeout(timer);
      }
    }

    async function removeOne(line) {
      var current = line;
      var lastError = null;

      for (var attempt = 0; attempt < 2; attempt += 1) {
        try {
          var response = await write(changeUrl, { id: current.key, quantity: 0 });
          if (!response || !response.ok) {
            lastError = makeError('E_SC_UPSELL_REMOVE_WRITE', 'Upsell removal failed', response && response.status);
          }
        } catch (error) {
          lastError = error;
        }

        var cart = await readCart();
        var remaining = ownedLines(cart);
        var sameKey = remaining.find(function (item) { return item.key === current.key; });
        if (!sameKey) return cart;
        if (attempt === 0) {
          current = sameKey;
          continue;
        }
      }

      throw lastError || makeError('E_SC_UPSELL_REMOVE_POSTCONDITION', 'Upsell line is still present');
    }

    async function removeAll(cart) {
      var lines = ownedLines(cart);
      if (lines.length > maxOwnedLines) {
        throw makeError('E_SC_UPSELL_DUPLICATE_LIMIT', 'Too many owned upsell lines to reconcile');
      }

      var current = cart;
      while (lines.length) {
        current = await removeOne(lines[0]);
        lines = ownedLines(current);
      }
      return current;
    }

    async function addOne(variantId) {
      var operationId = makeOperationId(options.randomId);
      var lastError = null;

      for (var attempt = 0; attempt < 2; attempt += 1) {
        var definiteFailure = false;
        var properties = {
          _sc_offer_id: offerId,
          _sc_offer_kind: offerKind,
          _sc_offer_operation_id: operationId
        };
        if (triggerProductId) properties._sc_offer_trigger_product_id = triggerProductId;
        if (parentOperationId) properties._sc_offer_parent_operation_id = parentOperationId;

        try {
          var response = await write(addUrl, {
            items: [{ id: Number(variantId), quantity: 1, properties: properties }]
          });
          if (!response || !response.ok) {
            lastError = makeError('E_SC_UPSELL_ADD_WRITE', 'Upsell add failed', response && response.status);
            definiteFailure = !!(response && response.status >= 400 && response.status < 500
              && response.status !== 408 && response.status !== 429);
          }
        } catch (error) {
          lastError = error;
        }

        var cart = await readCart();
        var lines = ownedLines(cart);
        if (exactLine(lines, variantId, operationId)) {
          if (options.onConfirmed) {
            options.onConfirmed({
              action: 'add',
              offerId: offerId,
              operationId: operationId,
              parentOperationId: parentOperationId,
              parentKey: parentKey,
              variantId: String(variantId),
              item: lines[0]
            });
          }
          return cart;
        }

        if (lines.length) {
          throw makeError('E_SC_UPSELL_ADD_POSTCONDITION', 'Upsell add produced an ambiguous owned line set');
        }
        if (definiteFailure) break;
        if (attempt === 0) continue;
      }

      throw lastError || makeError('E_SC_UPSELL_ADD_ABSENT', 'Upsell line is absent after the bounded retry');
    }

    async function converge(desired) {
      var target = copyDesired(desired);
      var cart = await readCart();
      var lines = ownedLines(cart);

      if (!target.checked) {
        if (!lines.length) return cart;
        var operationId = makeOperationId(options.randomId);
        var removed = await removeAll(cart);
        if (options.onConfirmed) {
          options.onConfirmed({
            action: 'remove',
            offerId: offerId,
            operationId: operationId,
            parentOperationId: parentOperationId,
            parentKey: parentKey,
            variantId: target.variantId
          });
        }
        return removed;
      }

      if (exactLine(lines, target.variantId)) return cart;
      if (lines.length) cart = await removeAll(cart);
      return addOne(target.variantId);
    }

    return {
      converge: converge,
      readCart: readCart,
      ownedLines: ownedLines
    };
  }

  function makeOperationId(randomId) {
    if (typeof randomId === 'function') return String(randomId());
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'sc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
  }

  function makeError(code, message, status) {
    var error = new Error(message);
    error.code = code;
    if (status) error.status = status;
    return error;
  }

  return {
    createSerialQueue: createSerialQueue,
    createIntentController: createIntentController,
    createCartTransport: createCartTransport,
    normalizeOfferConfig: normalizeOfferConfig,
    makeOperationId: makeOperationId
  };
});
